import CoworkDashboard from './CoworkDashboard';
import { dashboardCollections, dashboardPeriodRange, DashboardPeriod } from './dashboardCollections';
import { Link } from 'react-router-dom';
import AdminPageHeader from '../admin/AdminPageHeader';
// AdminPage.tsx — Co Marca Admin Dashboard — con Ventas de Cafetería integradas
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { isReservationCancelled } from '../../utils/reservationStatus';
import { fetchCitas, fetchClientesRetorno, Cita, ClienteRetorno } from '../../services/citasService';
import { fetchSucursales, Sucursal } from '../../services/pos/sucursalService';
import { fetchPuntosDeVenta, PuntoDeVenta } from '../../services/pos/puntoDeVentaService';
import { fetchSesionesPOS, SesionPOS } from '../../services/pos/sesionPOSService';
import { fetchAdministrativeReport } from '../../services/administrativeReportsService';
import { boliviaDateStamp, buildDashboardFinancials, DashboardPayment, parseDashboardPaymentRows } from './dashboardFinancials';
import {
    Banknote, BarChart2, TrendingUp, Users, DollarSign,
    Calendar, CheckCircle, AlertCircle, PieChart, Target, User,
    Search, RefreshCcw, Building, Filter, ChevronDown, Clock,
} from 'lucide-react';
import {
    ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
    BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { format, subDays, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import Loading from '../Loading';

// ─── Helpers ────────────────────────────────────────────────────────────────
const parsearFechaDesdeInput = (f: string): Date => {
    const [y, m, d] = f.split('-').map(Number);
    return new Date(y, m - 1, d);
};
const convertirFechaServidorALocal = (s: string): Date => {
    if (!s) return new Date();
    const f = new Date(s);
    return new Date(f.getUTCFullYear(), f.getUTCMonth(), f.getUTCDate(),
        f.getUTCHours(), f.getUTCMinutes(), f.getUTCSeconds());
};
const formatoFechaLocal = (s?: string | null) => {
    if (!s) return '-';
    try { return format(convertirFechaServidorALocal(s), "d 'de' MMMM, yyyy", { locale: es }); } catch { return s; }
};
const formatoFechaCorta = (s?: string | null) => {
    if (!s) return '-';
    try { return format(convertirFechaServidorALocal(s), 'dd/MM/yyyy', { locale: es }); } catch { return s; }
};
const formatoFechaGrafico = (s?: string | null) => {
    if (!s) return '-';
    try { return format(convertirFechaServidorALocal(s), 'dd/MM', { locale: es }); } catch { return s; }
};
const formatoMoneda = (n: number) =>
    new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB', minimumFractionDigits: 2 }).format(n);

// ─── Colores Co Marca ───────────────────────────────────────────────────────
const colors = {
    primary:       'var(--ad-primary, #3564db)',
    primaryDark:   'var(--ad-primary-dark, #8f1f64)',
    secondary:     'var(--ad-secondary, #3564db)',
    secondaryLight:'var(--ad-secondary, #92b1ed)',
    accent:        '#ffd97c',
    accentDark:    '#ea8406',
};

// ─── CSS ─────────────────────────────────────────────────────────────────────
const getCss = (isDark: boolean) => `


.adm-dash { font-family: 'DM Sans', sans-serif; color: ${isDark ? 'var(--ad-ink, #f1f5f9)' : 'var(--ad-ink, #0f172a)'}; }

.custom-scroll::-webkit-scrollbar { width: 6px; }
.custom-scroll::-webkit-scrollbar-track { background: transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color: rgba(97, 37, 123,0.3); border-radius: 20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(97, 37, 123,0.5); }

@keyframes sectionFadeUp {
    from { opacity:0; transform:translateY(22px); }
    to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
    0%,100% { box-shadow:0 0 0 0 rgba(var(--ad-primary-rgb, 193, 52, 139),.35); }
    50%     { box-shadow:0 0 0 5px rgba(var(--ad-primary-rgb, 193, 52, 139),0); }
}
@keyframes blobFloat {
    0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; }
    50%     { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; }
}
@keyframes spin { to { transform:rotate(360deg); } }
@keyframes slideDown { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }

.adm-fadeup   { animation: sectionFadeUp .55s cubic-bezier(.16,1,.3,1) both; }
.adm-fadeup-1 { animation: sectionFadeUp .55s .08s cubic-bezier(.16,1,.3,1) both; }
.adm-fadeup-2 { animation: sectionFadeUp .55s .16s cubic-bezier(.16,1,.3,1) both; }
.adm-fadeup-3 { animation: sectionFadeUp .55s .24s cubic-bezier(.16,1,.3,1) both; }
.adm-fadeup-4 { animation: sectionFadeUp .55s .32s cubic-bezier(.16,1,.3,1) both; }

.adm-card {
    background: ${isDark ? 'var(--ad-surface, #150a28)' : '#fff'};
    border-radius: 20px;
    border: 1.5px solid ${isDark ? 'rgba(var(--ad-primary-rgb, 193, 52, 139),0.28)' : 'var(--ad-line, #f1f5f9)'};
    transition: all .3s cubic-bezier(.16,1,.3,1);
    color: ${isDark ? 'var(--ad-ink, #f1f5f9)' : 'var(--ad-ink, #0f172a)'};
}
.adm-card:hover {
    transform: translateY(-3px);
    box-shadow: ${isDark ? '0 20px 48px rgba(0,0,0,.6)' : '0 20px 48px rgba(0,0,0,.07)'};
}

.adm-metric-card {
    background: ${isDark ? 'var(--ad-surface, #150a28)' : '#fff'};
    border-radius: 20px;
    border: 1.5px solid ${isDark ? 'rgba(var(--ad-primary-rgb, 193, 52, 139),0.28)' : 'var(--ad-line, #f1f5f9)'};
    padding: 22px 24px;
    transition: all .3s cubic-bezier(.16,1,.3,1);
    position: relative; overflow: hidden;
    color: ${isDark ? 'var(--ad-ink, #f1f5f9)' : 'var(--ad-ink, #0f172a)'};
}
.adm-metric-card::before {
    content:''; position:absolute; top:0; left:0; right:0;
    height: 3px;
    opacity: 0; transition: opacity .3s;
}
.adm-metric-card:hover { transform:translateY(-3px); box-shadow:${isDark ? '0 20px 48px rgba(0,0,0,.6)' : '0 20px 48px rgba(0,0,0,.07)'}; }
.adm-metric-card:hover::before { opacity:1; }

.adm-pill-btn {
    padding: 7px 16px; border-radius: 100px; font-size: .8rem; font-weight: 700;
    background: ${isDark ? 'rgba(255,255,255,0.08)' : '#fff'};
    border: 1.5px solid ${isDark ? 'rgba(var(--ad-primary-rgb, 193, 52, 139),0.35)' : 'var(--ad-line, #e2e8f0)'};
    color: ${isDark ? 'var(--ad-ink, #e2e8f0)' : 'var(--ad-ink, #374151)'};
    cursor: pointer;
    font-family: 'DM Sans', sans-serif; transition: all .2s;
}
.adm-pill-btn:hover {
    background: ${colors.primary}; color:#fff; border-color:${colors.primary};
    box-shadow: 0 4px 12px color-mix(in srgb, ${colors.primary} 15.69%, transparent);
}

.adm-input {
    width:100%; padding:9px 14px; border-radius:10px;
    border:1.5px solid ${isDark ? 'rgba(var(--ad-primary-rgb, 193, 52, 139),0.35)' : 'var(--ad-line, #e5e7eb)'};
    background: ${isDark ? '#1a0d30' : '#fafafa'};
    font-size:.85rem; color: ${isDark ? 'var(--ad-ink, #f8fafc)' : 'var(--ad-ink, #374151)'}; font-family:'DM Sans',sans-serif;
    outline:none; transition:all .2s;
}
.adm-input:focus { border-color:${colors.primary}; box-shadow:0 0 0 3px color-mix(in srgb, ${colors.primary} 7.06%, transparent); background: ${isDark ? '#21103d' : '#fff'}; }

.adm-slidedown { animation: slideDown .2s ease-out; }

.adm-row-hover { transition:background .15s; }
.adm-row-hover:hover { background: ${isDark ? 'rgba(var(--ad-primary-rgb, 193, 52, 139),0.15)' : 'var(--ad-soft, #f8fafc)'}; }
`;

// ─── Tooltips ────────────────────────────────────────────────────────────────
const TooltipBarra = ({ active, payload, label, isDark }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: 'var(--ad-surface)' ,
            border: isDark ? '1.5px solid rgba(var(--ad-primary-rgb, 193, 52, 139),0.4)' : '1.5px solid var(--ad-line, #f1f5f9)',
            borderRadius:'9px', padding:'12px 16px',
            boxShadow:'0 8px 32px rgba(0,0,0,.35)', fontFamily:'inherit'
        }} className="card custom-card vx-card">
            <p style={{ fontWeight:'700', color: 'var(--ad-ink)' , marginBottom:'6px', fontSize:'.82rem' }}>{label}</p>
            {payload.map((p: any, i: number) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:p.color }}/>
                    <span style={{ fontSize:'.78rem', color: isDark ? '#cbd5e1' : 'var(--ad-muted, #64748b)' }}>{p.name}</span>
                    <span style={{ fontWeight:'650', color: isDark ? '#ffffff' : 'var(--ad-ink, #0f172a)', marginLeft:'auto', fontSize:'.82rem' }}>{formatoMoneda(p.value)}</span>
                </div>
            ))}
        </div>
    );
};
const TooltipPie = ({ active, payload, isDark }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div style={{
            background: isDark ? '#1a0c32' : '#fff',
            border: isDark ? '1.5px solid rgba(var(--ad-primary-rgb, 193, 52, 139),0.4)' : '1.5px solid var(--ad-line, #f1f5f9)',
            borderRadius:'14px', padding:'12px 16px',
            boxShadow:'0 8px 32px rgba(0,0,0,.35)', fontFamily:"'DM Sans',sans-serif"
        }} className="card custom-card vx-card">
            <div style={{ display:'flex', alignItems:'center', gap:'7px', marginBottom:'4px' }}>
                <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:d.color }}/>
                <span style={{ fontWeight:'700', color: isDark ? '#ffd97c' : 'var(--ad-ink, #0f172a)', fontSize:'.82rem' }}>{d.name}</span>
            </div>
            <p style={{ fontWeight:'650', color: isDark ? '#ffffff' : 'var(--ad-ink, #0f172a)', fontSize:'1.1rem' }}>{formatoMoneda(d.value)}</p>
        </div>
    );
};

// ─── Componente principal ────────────────────────────────────────────────────
const StatsPanel = () => {
    const { isDark } = useTheme();
    const css = useMemo(() => getCss(isDark), [isDark]);

    const [citas, setCitas]                           = useState<Cita[]>([]);
    const [isLoading, setIsLoading]                   = useState(true);
    const [isLoadingPayments, setIsLoadingPayments]   = useState(false);
    const [paymentError, setPaymentError]             = useState('');

    const [clientesRetorno, setClientesRetorno]       = useState<ClienteRetorno[]>([]);
    const [loadingClientes, setLoadingClientes]       = useState(false);
    const [sucursales, setSucursales]                 = useState<Sucursal[]>([]);
    const [sucursalesActivas, setSucursalesActivas]   = useState<Sucursal[]>([]);
    const [sucursalSeleccionada, setSucursalSel]      = useState('todas');
    const [puntosDeVenta, setPuntosDeVenta]           = useState<PuntoDeVenta[]>([]);
    const [sesionesPOS, setSesionesPOS]               = useState<SesionPOS[]>([]);
    const [pagos, setPagos]                           = useState<DashboardPayment[]>([]);
    const [cafeteria, setCafeteria] = useState({ ventas: 0, total: 0, pagado: 0, saldo: 0 });
    const [pagosHoy, setPagosHoy]                     = useState<DashboardPayment[]>([]);
    const [periodo, setPeriodo] = useState<DashboardPeriod>('week');
    const filtrosRef = useRef<HTMLDetailsElement>(null);
    const [fechaInicio, setFechaInicio]               = useState('');
    const [fechaFin, setFechaFin]                     = useState('');
    const [busqueda, setBusqueda]                     = useState('');
    const [pagosRecientes, setPagosRecientes]         = useState<DashboardPayment[]>([]);


    const [stats, setStats] = useState({
        totalCitas:0, totalPagado:0, promedioTicket:0, pagosRegistrados:0,
        deudaTotal:0, clientesActivos:0, citasHoy:0, totalPagadoHoy:0,
    });
    const [metrica, setMetrica] = useState({
        ingresosPorDia:[] as Array<{ fecha: string; ingresos: number }>,
        distribucionOrigen:[] as Array<{ name:string; value:number; color:string; count:number }>,
    });

    const formatDateForInput = (d: Date) => {
        const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), dd = String(d.getDate()).padStart(2,'0');
        return `${y}-${m}-${dd}`;
    };


    useEffect(() => { const range = dashboardPeriodRange('week'); setFechaInicio(range.start); setFechaFin(range.end); }, []);

    const cambiarPeriodo = (next: DashboardPeriod) => {
        setPeriodo(next);
        if (next === 'custom') {
            if (filtrosRef.current) { filtrosRef.current.open = true; filtrosRef.current.scrollIntoView?.({behavior:'smooth',block:'center'}); }
            return;
        }
        const range = dashboardPeriodRange(next);
        setFechaInicio(range.start); setFechaFin(range.end);
    };

    useEffect(() => {
        fetchSucursales().then(d => { setSucursales(d); setSucursalesActivas(d.filter((s:Sucursal) => s.activo)); }).catch((() => undefined));
    }, []);

    const filtrarPorSucursal = useCallback((list: Cita[], sid: string) => {
        if (sid === 'todas') return list;
        const n = parseInt(sid);
        return list.filter(c => {
            const branch=c.sucursal??c.sucursal_info?.id;if(branch!==null&&branch!==undefined)return Number(branch)===n;
            const ses = sesionesPOS.find(s => s.id === c.sesion_pos);
            if (!ses) return false;
            const pto = puntosDeVenta.find(p => p.id === ses.punto_de_venta);
            return pto?.sucursal === n;
        });
    }, [sesionesPOS, puntosDeVenta]);

    const aplicarFiltro = useCallback(() => {
        if (!fechaInicio || !fechaFin || fechaInicio > fechaFin) return;
        const enPeriodoCitas = citas.filter(c => {
            if (!c.fecha_inicio) return false;
            const day = String(c.fecha_inicio).slice(0, 10);
            return day >= fechaInicio && day <= fechaFin;
        });
        const filtradasCitas = filtrarPorSucursal(enPeriodoCitas, sucursalSeleccionada);
        const financial = buildDashboardFinancials(pagos);
        const todayFinancial = buildDashboardFinancials(pagosHoy);
        const hoy = boliviaDateStamp();
        const citasHoy = citas.filter(c => {
            if (!c.fecha_inicio) return false;
            return String(c.fecha_inicio).slice(0, 10) === hoy;
        });
        let deudaTotal = 0;
        filtradasCitas.forEach(c => { const total=Number(c.total)||0; deudaTotal += Math.max(0, total-(Number(c.monto_pagado)||0)); });
        setPagosRecientes([...pagos].sort((a,b)=>Date.parse(b.paidAt)-Date.parse(a.paidAt)).slice(0,100));
        setStats({
            totalCitas: filtradasCitas.length,
            totalPagado: Number(financial.total),
            promedioTicket: Number(financial.average),
            pagosRegistrados: financial.paymentCount,
            deudaTotal,
            clientesActivos: financial.uniquePayers,
            citasHoy: (sucursalSeleccionada === 'todas' ? citasHoy : filtrarPorSucursal(citasHoy, sucursalSeleccionada)).length,
            totalPagadoHoy: Number(todayFinancial.total),
        });
        setMetrica({
            ingresosPorDia: financial.byDay.map(day=>({ fecha: formatoFechaGrafico(day.date), ingresos:Number(day.amount) })),
            distribucionOrigen: financial.byOrigin.map((origin,index)=>({
                name:origin.label, value:Number(origin.amount), count:origin.count,
                color:[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight][index % 4],
            })),
        });
    }, [citas, pagos, pagosHoy, fechaInicio, fechaFin, sucursalSeleccionada, filtrarPorSucursal]);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            fetchCitas(),
            fetchClientesRetorno(),
            fetchPuntosDeVenta(),
            fetchSesionesPOS(),
        ])
            .then(([c, cl, pt, ses]) => {
                setCitas(c.filter(item => !isReservationCancelled(item)));
                setClientesRetorno(cl);
                setPuntosDeVenta(pt);
                setSesionesPOS(ses);
            })
            .catch((() => undefined))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        if (!fechaInicio || !fechaFin || fechaInicio > fechaFin) {
            setCafeteria({ ventas: 0, total: 0, pagado: 0, saldo: 0 });
            setPagos([]);
            setPagosHoy([]);
            setPagosRecientes([]);
            setStats({
                totalCitas:0, totalPagado:0, promedioTicket:0, pagosRegistrados:0,
                deudaTotal:0, clientesActivos:0, citasHoy:0, totalPagadoHoy:0,
            });
            setMetrica({ ingresosPorDia:[], distribucionOrigen:[] });
            setPaymentError(fechaInicio && fechaFin && fechaInicio > fechaFin ? 'La fecha inicial no puede ser posterior a la fecha final.' : '');
            setIsLoadingPayments(false);
            return;
        }
        let cancelled = false;
        const branch = sucursalSeleccionada === 'todas' ? {} : { sucursal_id: Number(sucursalSeleccionada) };
        const periodFilters = { fecha_desde: fechaInicio, fecha_hasta: fechaFin, ...branch };
        const today = boliviaDateStamp();
        const periodRequest = fetchAdministrativeReport('pagos', periodFilters);
        const todayRequest = fetchAdministrativeReport('pagos', { fecha_desde: today, fecha_hasta: today, ...branch });
        setIsLoadingPayments(true);
        setPaymentError('');
        Promise.all([periodRequest, todayRequest, fetchAdministrativeReport('cafeteria', periodFilters)])
            .then(([period, current, sales]) => {
                if (cancelled) return;
                setCafeteria({ ventas: Number(sales.summary.ventas) || 0, total: Number(sales.summary.total) || 0, pagado: Number(sales.summary.pagado) || 0, saldo: Number(sales.summary.saldo) || 0 });
                setPagos(parseDashboardPaymentRows(period.rows));
                setPagosHoy(parseDashboardPaymentRows(current.rows));
            })
            .catch(error => {
                if (cancelled) return;
                /* Browser diagnostics omitted. */
                setCafeteria({ ventas: 0, total: 0, pagado: 0, saldo: 0 });
            setPagos([]);
                setPagosHoy([]);
                setPaymentError('No se pudo validar la información financiera.');
            })
            .finally(() => { if (!cancelled) setIsLoadingPayments(false); });
        return () => { cancelled = true; };
    }, [fechaInicio, fechaFin, sucursalSeleccionada]);

    useEffect(() => { if (fechaInicio && fechaFin) aplicarFiltro(); }, [citas, pagos, pagosHoy, fechaInicio, fechaFin, sucursalSeleccionada, aplicarFiltro]);

    const loadClientes = useCallback(() => { setLoadingClientes(true); fetchClientesRetorno().then(setClientesRetorno).catch((() => undefined)).finally(()=>setLoadingClientes(false)); }, []);
    useEffect(() => { loadClientes(); }, [loadClientes]);

    const getNombreSucursal = () => {
        if (sucursalSeleccionada==='todas') return 'Todas las sucursales';
        const s = sucursales.find(x=>x.id.toString()===sucursalSeleccionada);
        return s ? s.nombre : 'Sucursal desconocida';
    };

    const clientesFrecuentes = useMemo(() => [...clientesRetorno].sort((a,b)=>b.cantidad_citas-a.cantidad_citas).slice(0,5), [clientesRetorno]);
    const financials = useMemo(() => buildDashboardFinancials(pagos), [pagos]);
    const collections = useMemo(() => dashboardCollections(pagos, fechaInicio, fechaFin), [pagos, fechaInicio, fechaFin]);
    const collectionSeries = [
        { key: 'reserva', label: 'Reservas', color: 'var(--ad-primary)' },
        { key: 'cafeteria', label: 'Cafetería', color: '#92b1ed' },
        { key: 'sesion', label: 'Sesiones', color: '#647fa8' },
        { key: 'administrativo', label: 'Administrativo', color: '#67a79b' },
    ].filter(series => series.key === 'reserva' || series.key === 'cafeteria' || pagos.some(payment => payment.origin === series.key && Number(payment.amount) > 0));




    const visibleReservations = useMemo(()=>filtrarPorSucursal(citas.filter(c=>!isReservationCancelled(c)),sucursalSeleccionada),[citas,sucursalSeleccionada,filtrarPorSucursal]);
    const upcomingReservations = useMemo(()=>visibleReservations.filter(c=>c.fecha_inicio && String(c.fecha_inicio).slice(0,10)>=boliviaDateStamp()).sort((a,b)=>`${a.fecha_inicio} ${a.hora_inicio}`.localeCompare(`${b.fecha_inicio} ${b.hora_inicio}`)).slice(0,3),[visibleReservations]);
    const serviceDistribution = useMemo(()=>{const counts:Record<string,number>={};visibleReservations.filter(c=>(!fechaInicio||String(c.fecha_inicio).slice(0,10)>=fechaInicio)&&(!fechaFin||String(c.fecha_inicio).slice(0,10)<=fechaFin)).forEach(c=>{const name=c.servicio_info?.nombre||'Sin servicio';counts[name]=(counts[name]||0)+1;});return Object.entries(counts).map(([name,value],i)=>({name,value,color:['#3564db','#92b1ed','#d5e1fa','#7292cf'][i%4]}));},[visibleReservations,fechaInicio,fechaFin]);
    if(isLoading)return <Loading text="Cargando dashboard…"/>;
    return <CoworkDashboard {...{stats,cafeteria,visibleReservations,upcomingReservations,collections,sucursalesActivas,sucursalSeleccionada,setSucursalSel,getNombreSucursal,periodo,cambiarPeriodo,fechaInicio,setFechaInicio,fechaFin,setFechaFin,paymentError,isLoadingPayments}}/>;
};
export default StatsPanel;
